import { ISourceDataNote, ISourceDataSubnote, NoteType } from '@/api/dtos';
import { ISourceDataFormFieldComponentProps, ISourceDataVerificationItem } from '@/utilities/forms/SourceDataFormProcessor';
import { faClipboardQuestion, faCalendarDays, faUser } from '@fortawesome/duotone-light-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Box, Dialog, Divider, IconButton, Paper, Slide, styled, Tab, Tabs, Toolbar, Typography, lighten, darken, Chip, Tooltip, alpha } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { FC, forwardRef, ReactElement, Ref, useMemo, useState, useEffect } from 'react';
import ConfigurationFormEditInfo, { ConfigurationTitle } from '../../configuration/edit/ConfigurationFormEditInfo';
import SourceDataInfo, { ISourceDataInfoProps, SourceDataBox, SourceDataTitle } from './SourceDataInfo';
import { NoteStatus } from '../../../../api/dtos';
import { DateTime } from 'luxon'

const NoteStatusChip = styled(Chip)(({theme}) => ({
    marginLeft: 'auto',
}));


const NoteDateChip = styled(Chip)(({theme}) => ({
    '& svg': {
        transform: 'scale(0.75)'
    },
    marginLeft: theme.spacing(2)
}));

const NoteDividerBox = styled(Box)(({theme}) => ({
    margin: theme.spacing(0, -2)
}));

const NoteDetails = styled(Box)(({theme}) => ({
    whiteSpace: 'pre-wrap',
    padding: theme.spacing(2),
    marginLeft: theme.spacing(6),
    borderLeft: theme.palette.mode === 'dark' ? 
        `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
        `solid 1px ${alpha(theme.palette.common.black, 0.12)}`
}));

const SubnoteBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(6),
    marginRight: theme.spacing(-2),
    
    padding: theme.spacing(2, 2, 0, 2)
}));

const SubnoteTitleBox = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(-2, -2, 0, -2),
    padding: theme.spacing(2),
    borderLeft: theme.palette.mode === 'dark' ? 
        `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
        `solid 1px ${alpha(theme.palette.common.black, 0.12)}`
}));

const SourceDataSubnote: FC<ISourceDataSubnote & {last: boolean}> = ({
    status,
    title,
    note,
    createdDate,
    creator,
    last
}) => {

    const statusLabel =  useMemo(() => {
        switch (status) {
            case NoteStatus.New:
                return <NoteStatusChip size="small" label="New" color="error" />;
            case NoteStatus.Updated:
                return <NoteStatusChip size="small" label="Update" color="warning" />;
            case NoteStatus.ResolutionProposed:
                return <NoteStatusChip size="small" label="Resolution Proposed" color="info" />;
            case NoteStatus.Closed:
                return <NoteStatusChip size="small" label="Closed" color="success" />;
            default:
                return null;
        }
    }, [status]);

    return (
        <SubnoteBox>
            <SubnoteTitleBox>
                {title}
                <Box sx={{ml: 'auto'}}>
                    {
                        !!statusLabel && (
                            <Tooltip title="Status">
                                {statusLabel}
                            </Tooltip>
                        )
                    }
                    {
                        !!createdDate && (
                            <Tooltip title="Date Created">
                                <NoteDateChip size="small" label={DateTime.fromISO(createdDate).toFormat('dd/MM/yyyy')} icon={<FontAwesomeIcon icon={faCalendarDays} />}/>
                            </Tooltip>
                        ) 
                    }
                    {
                        !!creator && (
                            <Tooltip title="Created By">
                                <NoteDateChip size="small" label={creator} icon={<FontAwesomeIcon icon={faUser} />}/>
                            </Tooltip>
                        ) 
                    }
                </Box>
            </SubnoteTitleBox>
            {
                !!note && (
                    <>
                        <NoteDividerBox>
                            <Divider />
                        </NoteDividerBox>
                        <NoteDetails>
                            {note}
                        </NoteDetails>
                    </>  
                )
            }
            {
                !last && (
                    <NoteDividerBox>
                        <Divider />
                    </NoteDividerBox>
                )
            }
        </SubnoteBox>
    )
}

export default SourceDataSubnote;